import {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import styled, { css } from 'styled-components';
import { Button } from 'antd';
import { LuMaximize2, LuPause, LuPlay } from 'react-icons/lu';
import { frameAt } from './tracksModel';
import { frameAt as stillAt } from '@utils/video/tsPreview';

/**
 * The analysed clip with the detections drawn over it on a canvas (nothing is burnt into the video): a small,
 * see-through dot per animal, a short trail, a pulse when an animal is first counted, and the counting line, which
 * can be dragged when `lineEditable`. Paints once per video frame (requestVideoFrameCallback where available).
 * `pip` is a second view (the flight map) in the corner of the picture; `swapped` puts it on the stage and the
 * video in the corner, and `onSwap` shows the button that switches the two. Both stay mounted either way.
 */

const GREEN = '32, 172, 107';
const LIGHT_GREEN = '144, 215, 173';
const AMBER = '232, 170, 60';
const RED = '235, 87, 87';
const TRAIL_SECONDS = 1;
const PULSE_FRAMES = 8;
const HANDLE_PX = 14;
// why Play is off (canPlay false): the button's tooltip and its name for a screen reader
const WAIT_TO_PLAY = 'The video plays when the analysis is done';

const Wrap = styled.div`
	position: relative;
	width: 100%;
	background: #050605;
	border: 1px solid rgba(70, 74, 70, 0.75);
	border-radius: 16px;
	overflow: hidden;
	aspect-ratio: ${({ $ratio }) => $ratio || '16 / 9'};
	max-height: 100%;
`;

// the small view in a corner of the stage: the map bottom-right over the video, or the video bottom-left over
// the map, where the map keeps its zoom and scale controls in the right corner
const cornerCss = css`
	top: auto;
	bottom: ${({ theme }) => theme.token.marginSM}px;
	width: 38%;
	border-radius: ${({ theme }) => theme.token.borderRadiusLG}px;
	overflow: hidden;
	box-shadow: ${({ theme }) => theme.token.boxShadowSecondary};
	z-index: 3;
`;

const Picture = styled.div`
	position: absolute;
	inset: 0;
	${({ $pip, $ratio, theme }) =>
		$pip &&
		css`
			${cornerCss};
			right: auto;
			left: ${theme.token.marginSM}px;
			aspect-ratio: ${$ratio || '16 / 9'};
			border: ${theme.token.lineWidth}px ${theme.token.lineType} ${theme.token.colorBorder};
		`}
`;

const PipSlot = styled.div`
	position: absolute;
	inset: 0;
	z-index: 1;
	${({ $pip, theme }) =>
		$pip &&
		css`
			${cornerCss};
			left: auto;
			right: ${theme.token.marginSM}px;
			aspect-ratio: 4 / 3;
		`}
`;

const SwapButton = styled(Button)`
	position: absolute;
	top: ${({ theme }) => theme.token.marginXS}px;
	right: ${({ theme }) => theme.token.marginXS}px;
	z-index: 4;
`;

const Video = styled.video`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
	display: block;
`;

// a video the browser cannot play (.ts) shows still frames from it instead
const Still = styled.img`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	object-fit: contain;
	display: block;
`;

const Canvas = styled.canvas`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
	cursor: ${({ $cursor }) => $cursor || 'default'};
	touch-action: none;
`;

const Controls = styled.div`
	display: flex;
	flex-wrap: wrap;
	row-gap: ${({ theme }) => theme.token.marginXS}px;
	align-items: center;
	gap: 0.875rem;
	margin-top: 0.75rem;
	min-height: 52px;
	padding: 0.4rem 1.1rem;
	background: rgba(28, 30, 28, 0.86);
	border: 1px solid rgba(70, 74, 70, 0.75);
	border-radius: 14px;
	color: #b4b9b4;
	font-variant-numeric: tabular-nums;
	font-size: 0.8rem;
`;

const PlayButton = styled.button`
	display: grid;
	place-items: center;
	flex: none;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	border: 0;
	background: ${({ theme }) => theme.colors.darkGreen};
	color: #fff;
	cursor: pointer;
	&:hover {
		background: ${({ theme }) => theme.colors.green};
	}
	&:disabled {
		background: ${({ theme }) => theme.token.colorBgContainerDisabled};
		color: ${({ theme }) => theme.token.colorTextDisabled};
		cursor: not-allowed;
	}
`;

const Scrub = styled.input`
	width: 100%;
	accent-color: ${({ theme }) => theme.colors.green};
	min-width: 0;
`;

// the seek bar with a tick above it at each frame someone corrected (green added, red removed)
const ScrubWrap = styled.div`
	position: relative;
	flex: 1;
	min-width: ${({ theme }) => theme.token.controlHeight * 4}px;
	display: flex;
	align-items: center;
`;

// the player's toggles and actions (chips), on their own row under the seek bar so the bar keeps its width
const ChipRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${({ theme }) => theme.token.marginXS}px;
	flex-basis: 100%;
`;

const EditMark = styled.span`
	position: absolute;
	top: ${({ theme }) => -theme.token.sizeXXS}px;
	width: ${({ theme }) => theme.token.lineWidthBold}px;
	height: ${({ theme }) => theme.token.sizeXS}px;
	border-radius: ${({ theme }) => theme.token.borderRadiusXS}px;
	background: ${({ theme, $kind }) =>
		$kind === 'remove' ? theme.token.colorError : theme.token.colorSuccess};
	pointer-events: none;
`;

const Speed = styled.select`
	background: ${({ theme }) => theme.colors.backgroundInput};
	color: ${({ theme }) => theme.colors.textColor};
	border: 1px solid ${({ theme }) => theme.colors.borderInput};
	border-radius: 6px;
	padding: 2px 4px;
`;

const fmt = s => {
	if (!Number.isFinite(s)) return '0:00.0';
	const m = Math.floor(s / 60);
	return `${m}:${(s % 60).toFixed(1).padStart(4, '0')}`;
};

/** Where the video picture sits inside the element (object-fit: contain letterboxing), in CSS pixels. */
const contentRect = (el, vw, vh) => {
	const w = el.clientWidth;
	const h = el.clientHeight;
	if (!vw || !vh) return { x: 0, y: 0, w, h };
	const s = Math.min(w / vw, h / vh);
	return { x: (w - vw * s) / 2, y: (h - vh * s) / 2, w: vw * s, h: vh * s };
};

const OverlayPlayer = forwardRef(
	(
		{
			src,
			index,
			line,
			lineEditable = false,
			onLineChange,
			onFrame,
			aspect,
			showDetections = true,
			showTrails = true,
			// corrections: called with { id, frame, x, y } (x, y in CSS pixels of the picture) when a dot is clicked;
			// selectedIds get a white ring, tracks a person added an amber one
			onPick = null,
			selectedIds = null,
			// the assistant's proposals, dashed: { negative: track id } or { frame, box: [x1, y1, x2, y2] } (an add)
			proposals = null,
			// correcting: 'click' (onAddPoint(frame, [x, y])) or 'box' (drag, onAddBox(frame, [x1, y1, x2, y2])), in
			// source pixels; pendingAdds ({ frame, box | point, status }) are objects added and not followed yet
			tool = null,
			onAddPoint = null,
			onAddBox = null,
			pendingAdds = null,
			// detections drawn as 'dot' or 'box'
			marker = 'dot',
			// several kinds: { label: { rgb } } a colour each (kinds.kindColors); else everything green
			kindColors = null,
			// the video starts playing when it is opened (muted, so browsers allow it)
			autoPlay = true,
			// false: Play is off and says why, and a click on the picture does not play the video (autoPlay is
			// separate); the video still pauses, seeks and steps
			canPlay = true,
			// ticks on the seek bar: [{ sec, kind: 'add' | 'remove' }]
			editMarks = null,
			extraControls = null,
			pip = null,
			swapped = false,
			onSwap,
			stills = null,
			stillsDuration = 0,
			children,
		},
		ref
	) => {
		const wrapRef = useRef(null);
		const videoRef = useRef(null);
		const canvasRef = useRef(null);
		const controlsRef = useRef(null);
		const frameRef = useRef(0);
		const dragRef = useRef(null);
		const boxDragRef = useRef(null);
		const [playing, setPlaying] = useState(false);
		const [time, setTime] = useState(0);
		const [duration, setDuration] = useState(0);
		const [cursor, setCursor] = useState('default');
		const [stillTime, setStillTime] = useState(0);
		const still = stills ? stillAt(stills, stillTime) : null;

		useImperativeHandle(ref, () => ({
			seekTo: sec => {
				if (stills) setStillTime(Math.max(0, sec));
				else if (videoRef.current) videoRef.current.currentTime = Math.max(0, sec);
			},
			video: () => videoRef.current,
			// the time shown: the still frame's for a video that only has stills
			now: () => (stills ? (still?.t ?? 0) : videoRef.current?.currentTime || 0),
			// the control bar under the picture, for a parent that fits the picture into a fixed height
			controls: () => controlsRef.current,
		}));

		// source pixels (tracks.json) → canvas device pixels
		const mapping = useCallback(() => {
			const wrap = wrapRef.current;
			const v = videoRef.current;
			if (!wrap || !v || !index) return null;
			const r = contentRect(wrap, v.videoWidth || index.width, v.videoHeight || index.height);
			const dpr = window.devicePixelRatio || 1;
			const s = (r.w / index.width) * dpr;
			return { s, ox: r.x * dpr, oy: r.y * dpr, dpr, r };
		}, [index]);

		const paint = useCallback(
			frame => {
				const canvas = canvasRef.current;
				const wrap = wrapRef.current;
				if (!canvas || !wrap) return;
				const dpr = window.devicePixelRatio || 1;
				const W = Math.round(wrap.clientWidth * dpr);
				const H = Math.round(wrap.clientHeight * dpr);
				if (canvas.width !== W || canvas.height !== H) {
					canvas.width = W;
					canvas.height = H;
				}
				const ctx = canvas.getContext('2d');
				ctx.clearRect(0, 0, W, H);
				const m = mapping();
				if (!m || !index) return;
				const drag = boxDragRef.current;
				if (drag) {
					ctx.save();
					ctx.setLineDash([6 * dpr, 4 * dpr]);
					ctx.lineWidth = 2 * dpr;
					ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
					const [ax, ay] = [m.ox + drag.from[0] * m.s, m.oy + drag.from[1] * m.s];
					const [bx, by] = [m.ox + drag.to[0] * m.s, m.oy + drag.to[1] * m.s];
					ctx.strokeRect(
						Math.min(ax, bx),
						Math.min(ay, by),
						Math.abs(bx - ax),
						Math.abs(by - ay)
					);
					ctx.restore();
				}
				const X = x => m.ox + x * m.s;
				const Y = y => m.oy + y * m.s;
				const { perFrame, trackOf, pointOf, tracks, fps } = index;
				const f = Math.min(index.nFrames - 1, Math.max(0, frame));
				const lo = perFrame[f];
				const hi = perFrame[f + 1];
				const trail = Math.max(2, Math.round(TRAIL_SECONDS * fps));
				const colorOf = tr => kindColors?.[tr.label]?.rgb || GREEN;

				if (showDetections) {
					if (showTrails) {
						// trails: the last TRAIL_SECONDS of each visible animal, older parts fainter, cut at jumps over
						// half a body length (a tracker hop between neighbours is not a path)
						const BANDS = 4;
						const pathsOf = new Map();
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const j = pointOf[i];
							const rgb = colorOf(tr);
							if (!pathsOf.has(rgb))
								pathsOf.set(
									rgb,
									Array.from({ length: BANDS }, () => new Path2D())
								);
							const paths = pathsOf.get(rgb);
							const body = 0.5 * Math.sqrt(Math.max(1, tr.w[j] * tr.h[j]));
							for (let k = j; k > 0; k -= 1) {
								const age = f - tr.t[k - 1];
								if (age > trail) break;
								if (Math.hypot(tr.x[k] - tr.x[k - 1], tr.y[k] - tr.y[k - 1]) > body)
									break;
								const path =
									paths[Math.min(BANDS - 1, Math.floor((age / trail) * BANDS))];
								path.moveTo(X(tr.x[k]), Y(tr.y[k]));
								path.lineTo(X(tr.x[k - 1]), Y(tr.y[k - 1]));
							}
						}
						ctx.lineWidth = 2.5 * dpr;
						ctx.lineCap = 'round';
						pathsOf.forEach((paths, rgb) =>
							paths.forEach((path, band) => {
								ctx.strokeStyle = `rgba(${rgb}, ${0.8 * (1 - band / BANDS)})`;
								ctx.stroke(path);
							})
						);
					}

					const picked = selectedIds?.length ? new Set(selectedIds) : null;
					if (marker === 'box') {
						// boxes: the object's outline, light green for what a person added, white when picked
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const j = pointOf[i];
							const sel = picked?.has(tr.id);
							const x1 = X(tr.x[j] - tr.w[j] / 2);
							const y1 = Y(tr.y[j] - tr.h[j] / 2);
							const bw = tr.w[j] * m.s;
							const bh = tr.h[j] * m.s;
							ctx.fillStyle = `rgba(${colorOf(tr)}, 0.08)`;
							ctx.fillRect(x1, y1, bw, bh);
							ctx.strokeStyle = sel
								? 'rgba(255, 255, 255, 0.95)'
								: tr.added
									? `rgba(${LIGHT_GREEN}, 0.95)`
									: `rgba(${colorOf(tr)}, 0.9)`;
							ctx.lineWidth = (sel ? 2.5 : 2) * dpr;
							ctx.strokeRect(x1, y1, bw, bh);
						}
					} else {
						// dots: small and see-through so the animal stays visible
						const r = Math.max(3, 3.5 * dpr);
						const dotsOf = new Map();
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const j = pointOf[i];
							const rgb = colorOf(tr);
							if (!dotsOf.has(rgb)) dotsOf.set(rgb, new Path2D());
							const cx = X(tr.x[j]);
							const cy = Y(tr.y[j]);
							dotsOf.get(rgb).moveTo(cx + r, cy);
							dotsOf.get(rgb).arc(cx, cy, r, 0, Math.PI * 2);
						}
						ctx.strokeStyle = 'rgba(15, 15, 15, 0.6)';
						ctx.lineWidth = 1 * dpr;
						dotsOf.forEach((path, rgb) => {
							ctx.fillStyle = `rgba(${rgb}, 0.65)`;
							ctx.fill(path);
							ctx.stroke(path);
						});

						// rings: what a person added (light green) and what is picked for a correction (white)
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							const sel = picked?.has(tr.id);
							if (!tr.added && !sel) continue;
							const j = pointOf[i];
							ctx.strokeStyle = sel
								? 'rgba(255, 255, 255, 0.95)'
								: `rgba(${LIGHT_GREEN}, 0.95)`;
							ctx.lineWidth = (sel ? 2.5 : 2) * dpr;
							ctx.beginPath();
							ctx.arc(X(tr.x[j]), Y(tr.y[j]), 11 * dpr, 0, Math.PI * 2);
							ctx.stroke();
						}
					}

					// the assistant's proposals: a red dashed ring on a dot it would take out, a light-green dashed box where
					// it saw one without a dot
					if (proposals?.length) {
						ctx.save();
						ctx.setLineDash([5 * dpr, 4 * dpr]);
						ctx.lineWidth = 2 * dpr;
						const out = new Map(
							proposals.filter(q => q.negative != null).map(q => [q.negative, q.n])
						);
						// the proposal's number beside its mark, white with a dark edge
						const number = (n, x, y) => {
							if (n == null) return;
							ctx.save();
							ctx.setLineDash([]);
							ctx.font = `800 ${14 * dpr}px sans-serif`;
							ctx.lineJoin = 'round';
							ctx.lineWidth = 4 * dpr;
							ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
							ctx.strokeText(String(n), x, y);
							ctx.fillStyle = 'rgba(255, 255, 255, 1)';
							ctx.fillText(String(n), x, y);
							ctx.restore();
						};
						ctx.strokeStyle = `rgba(${RED}, 0.95)`;
						for (let i = lo; i < hi; i += 1) {
							const tr = tracks[trackOf[i]];
							if (!out.has(tr.id)) continue;
							const j = pointOf[i];
							ctx.beginPath();
							ctx.arc(X(tr.x[j]), Y(tr.y[j]), 12 * dpr, 0, Math.PI * 2);
							ctx.stroke();
							number(out.get(tr.id), X(tr.x[j]) + 15 * dpr, Y(tr.y[j]) - 10 * dpr);
						}
						ctx.strokeStyle = `rgba(${LIGHT_GREEN}, 0.95)`;
						ctx.setLineDash([10 * dpr, 6 * dpr]);
						ctx.lineWidth = 3 * dpr;
						const near = Math.max(3, Math.round(fps / 3));
						proposals.forEach(q => {
							if (!q.box || Math.abs(q.frame - f) > near) return;
							const [x1, y1, x2, y2] = q.box;
							ctx.strokeRect(X(x1), Y(y1), X(x2) - X(x1), Y(y2) - Y(y1));
							number(q.n, X(x2) + 4 * dpr, Y(y1) + 4 * dpr);
						});
						ctx.restore();
					}

					// objects people added that are being followed (light-green dashed, breathing) or could not be followed
					// (amber)
					if (pendingAdds?.length) {
						const nearAdd = Math.max(3, Math.round(fps / 3));
						const breath = 0.45 + 0.5 * (0.5 + 0.5 * Math.sin(performance.now() / 190));
						ctx.save();
						ctx.lineWidth = 2 * dpr;
						ctx.font = `600 ${11 * dpr}px sans-serif`;
						ctx.lineJoin = 'round';
						pendingAdds.forEach(q => {
							if (Math.abs(q.frame - f) > nearAdd) return;
							const following = q.status === 'following';
							const asking = q.status === 'confirm';
							const rgb = asking ? '255, 255, 255' : following ? LIGHT_GREEN : AMBER;
							ctx.setLineDash(following || asking ? [6 * dpr, 4 * dpr] : []);
							ctx.strokeStyle = `rgba(${rgb}, ${following ? breath : 0.95})`;
							ctx.fillStyle = `rgba(${rgb}, 0.1)`;
							let lx;
							let ly;
							if (q.box) {
								const [x1, y1, x2, y2] = q.box;
								ctx.fillRect(X(x1), Y(y1), X(x2) - X(x1), Y(y2) - Y(y1));
								ctx.strokeRect(X(x1), Y(y1), X(x2) - X(x1), Y(y2) - Y(y1));
								[lx, ly] = [X(x2) + 6 * dpr, (Y(y1) + Y(y2)) / 2 + 4 * dpr];
							} else if (q.point) {
								ctx.beginPath();
								ctx.arc(X(q.point[0]), Y(q.point[1]), 14 * dpr, 0, Math.PI * 2);
								ctx.fill();
								ctx.stroke();
								[lx, ly] = [X(q.point[0]) + 20 * dpr, Y(q.point[1]) + 4 * dpr];
							} else return;
							// a white label with a dark edge, readable on snow and on forest
							const label =
								q.label ||
								(asking ? 'add?' : following ? 'following…' : 'could not follow');
							ctx.setLineDash([]);
							ctx.lineWidth = 3 * dpr;
							ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
							ctx.strokeText(label, lx, ly);
							ctx.fillStyle =
								following || asking
									? 'rgba(245, 245, 245, 1)'
									: `rgba(${AMBER}, 1)`;
							ctx.fillText(label, lx, ly);
							ctx.lineWidth = 2 * dpr;
						});
						ctx.restore();
					}

					// pulse when an animal is first counted
					for (let i = lo; i < hi; i += 1) {
						const tr = tracks[trackOf[i]];
						const age = f - (tr.confirmed_at ?? tr.t0);
						if (age < 0 || age >= PULSE_FRAMES) continue;
						const k = age / PULSE_FRAMES;
						const j = pointOf[i];
						ctx.strokeStyle = `rgba(${LIGHT_GREEN}, ${(1 - k) * 0.8})`;
						ctx.lineWidth = 2 * dpr;
						ctx.beginPath();
						ctx.arc(X(tr.x[j]), Y(tr.y[j]), (6 + 16 * k) * dpr, 0, Math.PI * 2);
						ctx.stroke();
					}
				}

				// counting line
				if (line) {
					ctx.save();
					ctx.setLineDash([15 * dpr, 11 * dpr]);
					ctx.strokeStyle = 'rgba(245, 245, 245, 0.95)';
					ctx.lineWidth = 3 * dpr;
					ctx.beginPath();
					ctx.moveTo(X(line.start[0]), Y(line.start[1]));
					ctx.lineTo(X(line.end[0]), Y(line.end[1]));
					ctx.stroke();
					ctx.restore();
					if (lineEditable) {
						for (const p of [line.start, line.end]) {
							ctx.fillStyle = 'rgba(245, 245, 245, 0.95)';
							ctx.strokeStyle = `rgba(${GREEN}, 1)`;
							ctx.lineWidth = 2 * dpr;
							ctx.beginPath();
							ctx.arc(X(p[0]), Y(p[1]), 7 * dpr, 0, Math.PI * 2);
							ctx.fill();
							ctx.stroke();
						}
					}
				}
			},
			[
				index,
				line,
				lineEditable,
				mapping,
				showDetections,
				showTrails,
				selectedIds,
				proposals,
				pendingAdds,
				marker,
				kindColors,
			]
		);

		// repaint on every presented video frame
		useEffect(() => {
			const v = videoRef.current;
			if (!v || !index) return undefined;
			let handle = null;
			let raf = null;
			const report = seconds => {
				const f = frameAt(index, seconds);
				if (f !== frameRef.current) {
					frameRef.current = f;
					onFrame?.(f);
				}
				paint(f);
			};
			if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
				const cb = (_now, meta) => {
					report(meta.mediaTime);
					handle = v.requestVideoFrameCallback(cb);
				};
				handle = v.requestVideoFrameCallback(cb);
			} else {
				const loop = () => {
					report(v.currentTime);
					raf = requestAnimationFrame(loop);
				};
				raf = requestAnimationFrame(loop);
			}
			const onSeeked = () => report(v.currentTime);
			v.addEventListener('seeked', onSeeked);
			v.addEventListener('loadeddata', onSeeked);
			return () => {
				if (handle != null && v.cancelVideoFrameCallback)
					v.cancelVideoFrameCallback(handle);
				if (raf != null) cancelAnimationFrame(raf);
				v.removeEventListener('seeked', onSeeked);
				v.removeEventListener('loadeddata', onSeeked);
			};
		}, [index, paint, onFrame]);

		// repaint when the layout or the line changes while paused
		useEffect(() => {
			paint(frameRef.current);
			const wrap = wrapRef.current;
			if (!wrap) return undefined;
			const ro = new ResizeObserver(() => paint(frameRef.current));
			ro.observe(wrap);
			return () => ro.disconnect();
		}, [paint]);

		// an added object being followed breathes, also while the video is paused
		const following = !!pendingAdds?.some(q => q.status === 'following');
		useEffect(() => {
			if (!following) return undefined;
			const id = setInterval(() => paint(frameRef.current), 120);
			return () => clearInterval(id);
		}, [following, paint]);

		// line editing: drag an end point, or the whole line
		const toSource = e => {
			const m = mapping();
			const rect = canvasRef.current.getBoundingClientRect();
			const cx = (e.clientX - rect.left) * m.dpr;
			const cy = (e.clientY - rect.top) * m.dpr;
			return [(cx - m.ox) / m.s, (cy - m.oy) / m.s];
		};
		const hit = p => {
			const m = mapping();
			const tol = (HANDLE_PX * m.dpr) / m.s;
			if (Math.hypot(p[0] - line.start[0], p[1] - line.start[1]) < tol) return 'start';
			if (Math.hypot(p[0] - line.end[0], p[1] - line.end[1]) < tol) return 'end';
			const [ax, ay] = line.start;
			const [bx, by] = line.end;
			const L2 = (bx - ax) ** 2 + (by - ay) ** 2 || 1;
			const u = Math.max(
				0,
				Math.min(1, ((p[0] - ax) * (bx - ax) + (p[1] - ay) * (by - ay)) / L2)
			);
			if (Math.hypot(p[0] - (ax + u * (bx - ax)), p[1] - (ay + u * (by - ay))) < tol)
				return 'line';
			return null;
		};
		const clampP = p => [
			Math.max(0, Math.min(index.width, Math.round(p[0]))),
			Math.max(0, Math.min(index.height, Math.round(p[1]))),
		];
		// where a point in source pixels is on the picture, in CSS pixels (for a card next to it)
		const screenAt = p => {
			const m = mapping();
			return {
				x: m.r.x + p[0] * (m.s / m.dpr),
				y: m.r.y + p[1] * (m.s / m.dpr),
				width: wrapRef.current?.clientWidth || 0,
				height: wrapRef.current?.clientHeight || 0,
			};
		};
		// the dot under a click on the frame shown, if any
		const pickAt = p => {
			const m = mapping();
			if (!m || !index) return null;
			const f = Math.min(index.nFrames - 1, Math.max(0, frameRef.current));
			let best = null;
			for (let i = index.perFrame[f]; i < index.perFrame[f + 1]; i += 1) {
				const tr = index.tracks[index.trackOf[i]];
				const j = index.pointOf[i];
				const tol = Math.max((HANDLE_PX * m.dpr) / m.s, 0.6 * Math.max(tr.w[j], tr.h[j]));
				const d = Math.hypot(p[0] - tr.x[j], p[1] - tr.y[j]);
				if (d <= tol && (!best || d < best.d)) best = { d, tr, j };
			}
			if (!best) return null;
			return {
				id: best.tr.id,
				frame: f,
				x: m.r.x + best.tr.x[best.j] * (m.s / m.dpr),
				y: m.r.y + best.tr.y[best.j] * (m.s / m.dpr),
				width: wrapRef.current?.clientWidth || 0,
				height: wrapRef.current?.clientHeight || 0,
			};
		};
		const onPointerDown = e => {
			if (onPick && showDetections) {
				const picked = pickAt(toSource(e));
				if (picked) {
					onPick(picked);
					return;
				}
			}
			if (tool === 'click' && onAddPoint) {
				const p = clampP(toSource(e));
				onAddPoint(frameRef.current, p, screenAt(p));
				return;
			}
			if (tool === 'box' && onAddBox) {
				const p0 = clampP(toSource(e));
				boxDragRef.current = { from: p0, to: p0 };
				e.currentTarget.setPointerCapture(e.pointerId);
				return;
			}
			if (tool) return;
			if (!lineEditable || !line) {
				toggle();
				return;
			}
			const p = toSource(e);
			const part = hit(p);
			dragRef.current = part
				? { part, from: p, line }
				: { part: 'new', from: clampP(p), line };
			e.currentTarget.setPointerCapture(e.pointerId);
		};
		const onPointerMove = e => {
			if (boxDragRef.current) {
				boxDragRef.current.to = clampP(toSource(e));
				paint(frameRef.current);
				return;
			}
			if (tool || !lineEditable || !line) return;
			const p = toSource(e);
			const d = dragRef.current;
			if (!d) {
				const part = hit(p);
				setCursor(part === 'line' ? 'move' : part ? 'grab' : 'crosshair');
				return;
			}
			let next;
			if (d.part === 'start') next = { start: clampP(p), end: d.line.end };
			else if (d.part === 'end') next = { start: d.line.start, end: clampP(p) };
			else if (d.part === 'line') {
				const dx = p[0] - d.from[0];
				const dy = p[1] - d.from[1];
				next = {
					start: clampP([d.line.start[0] + dx, d.line.start[1] + dy]),
					end: clampP([d.line.end[0] + dx, d.line.end[1] + dy]),
				};
			} else next = { start: d.from, end: clampP(p) };
			onLineChange?.(next);
		};
		const onPointerUp = () => {
			dragRef.current = null;
			const b = boxDragRef.current;
			if (b) {
				boxDragRef.current = null;
				const box = [
					Math.min(b.from[0], b.to[0]),
					Math.min(b.from[1], b.to[1]),
					Math.max(b.from[0], b.to[0]),
					Math.max(b.from[1], b.to[1]),
				];
				paint(frameRef.current);
				if (box[2] - box[0] >= 4 && box[3] - box[1] >= 4)
					onAddBox(frameRef.current, box, screenAt([box[2], box[1]]));
			}
		};

		const toggle = () => {
			const v = videoRef.current;
			if (!v) return;
			if (!v.paused) v.pause();
			else if (canPlay) v.play();
		};

		const showSwap = pip && onSwap;
		const swapButton = (
			<SwapButton
				size="small"
				shape="circle"
				icon={<LuMaximize2 />}
				onClick={onSwap}
				aria-label={swapped ? 'Show the video large' : 'Show the map large'}
				title={swapped ? 'Show the video large' : 'Show the map large'}
			/>
		);

		return (
			<div>
				<Wrap $ratio={aspect}>
					<Picture ref={wrapRef} $pip={!!pip && swapped} $ratio={aspect}>
						<Video
							ref={videoRef}
							src={stills ? undefined : src}
							preload="auto"
							autoPlay={autoPlay}
							playsInline
							muted
							onPlay={() => setPlaying(true)}
							onPause={() => setPlaying(false)}
							onTimeUpdate={e => setTime(e.currentTarget.currentTime)}
							onLoadedMetadata={e => setDuration(e.currentTarget.duration)}
							onClick={toggle}
						/>
						{still && <Still src={still.url} alt="" />}
						<Canvas
							ref={canvasRef}
							$interactive={lineEditable || !!onPick || !!tool}
							$cursor={tool === 'click' || tool === 'box' ? 'crosshair' : cursor}
							onPointerDown={onPointerDown}
							onPointerMove={onPointerMove}
							onPointerUp={onPointerUp}
							onPointerCancel={onPointerUp}
						/>
						{showSwap && swapped && swapButton}
					</Picture>
					{pip && (
						<PipSlot $pip={!swapped}>
							{pip}
							{showSwap && !swapped && swapButton}
						</PipSlot>
					)}
					{children}
				</Wrap>
				<Controls ref={controlsRef}>
					{!stills && (
						<PlayButton
							type="button"
							onClick={toggle}
							disabled={!canPlay}
							aria-label={canPlay ? (playing ? 'Pause' : 'Play') : WAIT_TO_PLAY}
							title={canPlay ? undefined : WAIT_TO_PLAY}>
							{playing ? <LuPause /> : <LuPlay />}
						</PlayButton>
					)}
					<ScrubWrap>
						<Scrub
							type="range"
							min={0}
							max={(stills ? stillsDuration : duration) || 0}
							step={stills ? 'any' : 1 / (index?.fps || 30)}
							value={stills ? stillTime : time}
							onChange={e => {
								if (stills) setStillTime(Number(e.target.value));
								else if (videoRef.current)
									videoRef.current.currentTime = Number(e.target.value);
							}}
							aria-label={stills ? 'Still frame' : 'Seek'}
						/>
						{!stills &&
							duration > 0 &&
							(editMarks || []).map((mk, i) => (
								<EditMark
									key={i}
									$kind={mk.kind}
									style={{ left: `${Math.min(100, (100 * mk.sec) / duration)}%` }}
								/>
							))}
					</ScrubWrap>
					<span>
						{fmt(stills ? (still?.t ?? 0) : time)} /{' '}
						{fmt(stills ? stillsDuration : duration)}
					</span>
					{!stills && (
						<Speed
							aria-label="Playback speed"
							defaultValue="1"
							onChange={e => {
								if (videoRef.current)
									videoRef.current.playbackRate = Number(e.target.value);
							}}>
							{[0.25, 0.5, 1, 2, 4].map(s => (
								<option key={s} value={s}>
									{s}×
								</option>
							))}
						</Speed>
					)}
					{extraControls && <ChipRow>{extraControls}</ChipRow>}
				</Controls>
			</div>
		);
	}
);

OverlayPlayer.displayName = 'OverlayPlayer';

export default memo(OverlayPlayer);
